import { APP_CLIENT_OAUTH_SCOPES, type AppRecord } from './types';

/**
 * Construction of the broker's `/oauth2/authorize` URL.
 *
 * One implementation, three callers: the CLI printing an app's sign-in URL, the admin API returning
 * it on the wire, and the admin console building its own PKCE sign-in redirect. Before this existed
 * the scope list was a literal in the CLI that happened to agree with APP_CLIENT_OAUTH_SCOPES with
 * nothing enforcing it - the same drift would reappear the moment a second builder existed.
 *
 * Zero runtime dependencies: this file is bundled into the browser build.
 */

export interface AuthorizeUrlParams {
  /** Cognito custom domain, e.g. id.apps.everyhome.org. */
  domainName: string;
  idpName: string;
  clientId: string;
  /**
   * Byte-exact. Cognito string-matches this against the registered callback, and normalization
   * appends a trailing slash to a bare origin, so a reconstructed value fails with
   * `redirect_mismatch` and no further detail.
   */
  redirectUri: string;
  scopes?: readonly string[];
  /** PKCE and state parameters, for callers running the flow rather than describing it. */
  extraParams?: Record<string, string>;
}

export function buildAuthorizeUrl(params: AuthorizeUrlParams): string {
  const url = new URL(`https://${params.domainName}/oauth2/authorize`);
  // Pinned so users are redirected straight to Entra and never see a Cognito provider chooser.
  url.searchParams.set('identity_provider', params.idpName);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('redirect_uri', params.redirectUri);
  url.searchParams.set('scope', (params.scopes ?? APP_CLIENT_OAUTH_SCOPES).join(' '));

  for (const [key, value] of Object.entries(params.extraParams ?? {})) {
    url.searchParams.set(key, value);
  }

  return url.toString();
}

export interface LogoutUrlParams {
  /** Cognito custom domain, e.g. id.apps.everyhome.org. */
  domainName: string;
  clientId: string;
  /**
   * Byte-exact, and it must be one of the app client's registered LogoutURLs. Cognito string-matches
   * this exactly as it does redirect_uri, and answers an unregistered value with `redirect_mismatch`
   * - the same opaque failure, on the endpoint people reach for last and test least.
   */
  logoutUri: string;
}

/**
 * The broker's `/logout` URL: the only way to clear Cognito's session cookie.
 *
 * Note the path is `/logout`, not `/oauth2/logout`, and that it takes `logout_uri` rather than
 * `post_logout_redirect_uri` - Cognito's endpoint is not the OIDC RP-initiated logout one, so a
 * library that builds the standard URL from discovery will not produce this.
 *
 * Two callers: the admin console signing itself out, and the implementation prompt telling another
 * app's developers how to do the same.
 */
export function buildLogoutUrl(params: LogoutUrlParams): string {
  const url = new URL(`https://${params.domainName}/logout`);
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('logout_uri', params.logoutUri);
  return url.toString();
}

/**
 * The sign-in URL to hand to an app's developers.
 *
 * Returns null when the record has no redirect URI. addApp/updateApp both require at least one, so
 * this should be unreachable - but the record comes from DynamoDB, which a person can edit by hand.
 * Callers should say so rather than render a URL with "undefined" in it that someone then debugs.
 */
export function buildLoginUrl(
  record: AppRecord,
  domainName: string,
  idpName: string,
): string | null {
  const redirectUri = record.redirect_uris?.[0];
  if (!redirectUri) return null;

  return buildAuthorizeUrl({
    domainName,
    idpName,
    clientId: record.client_id,
    redirectUri,
  });
}
